import { BASE_ATTACHMENTS, coralHighlight, mixCoralColors, nearestAttachment, reefAttachments, reefFishCount, REEF_HEIGHT, REEF_WIDTH, type CoralForm, type CoralIntent, type ReefState } from "./coral-chorus-core";

export const CORAL_ICONS: Record<CoralForm, string> = {
  antlers: '<path d="M24 44Q26 29 23 15M24 32Q13 29 9 17M17 26L17 12M24 25Q34 20 38 7M31 21L31 10M23 19L16 5"/>',
  tendrils: '<path d="M24 43C5 33 5 15 15 15S22 27 14 25M24 43C43 34 45 11 34 10S27 22 35 20M24 43C21 34 17 8 26 6S34 16 27 17"/>',
  fan: '<path d="M24 44L21 33C4 31 1 16 8 10C12 3 19 8 24 5C30 2 36 7 40 11C47 22 38 31 27 33ZM24 39L9 13M24 39L24 9M24 39L39 14M24 27L15 8M24 27L33 8"/>',
  plates: '<path d="M24 43L24 10M7 35Q24 27 41 35Q25 43 7 35ZM10 24Q24 16 38 24Q24 32 10 24ZM15 13Q24 7 33 13Q24 19 15 13Z"/>',
  cluster: '<path d="M6 35C1 22 16 17 20 23C14 3 35 2 34 21C47 15 51 39 39 42C30 48 10 46 6 35Z"/><path d="M16 30Q18 26 23 29M29 34Q32 30 36 33M23 16L24 17"/>',
  tubes: '<path d="M10 42L7 20Q13 14 19 20L19 40M19 42L18 8Q25 2 30 8L30 42M29 42L31 24Q39 18 43 24L39 42M7 20Q13 25 19 20M18 8Q24 13 30 8M31 24Q37 29 43 24"/>',
};
type Ctx = CanvasRenderingContext2D;
const TAU = Math.PI * 2;
function random(seed: number) {
  let value = seed >>> 0;
  return () => { value = (Math.imul(value, 1664525) + 1013904223) >>> 0; return value / 4294967296; };
}
function ellipse(ctx: Ctx, x: number, y: number, rx: number, ry: number, color: string | CanvasGradient, rotation = 0) {
  ctx.beginPath(); ctx.ellipse(x, y, rx, ry, rotation, 0, TAU); ctx.fillStyle = color; ctx.fill();
}
function path(ctx: Ctx, points: string, width: number, color: string | CanvasGradient) {
  ctx.strokeStyle = color; ctx.lineWidth = width; ctx.lineCap = "round"; ctx.lineJoin = "round";
  ctx.stroke(new Path2D(points));
}

export function drawCoral(ctx: Ctx, piece: CoralIntent, highlight: string) {
  ctx.save(); ctx.translate(piece.x, piece.y); ctx.rotate(piece.rotation * Math.PI / 180); ctx.scale(piece.size, piece.size);
  const color = piece.color;
  const gradient = ctx.createLinearGradient(0, .08, 0, -1.05);
  gradient.addColorStop(0, mixCoralColors(color, "#163f50", .38)); gradient.addColorStop(.28, color);
  gradient.addColorStop(.62, color); gradient.addColorStop(1, highlight);
  ctx.fillStyle = gradient; ctx.strokeStyle = gradient;
  if (piece.form === "antlers") {
    const branches: [string, number][] = [
      ["M0 .03 Q.04 -.23 0 -.47 Q-.04 -.73 -.08 -1", .09],
      ["M0 -.3 Q-.25 -.37 -.29 -.65 Q-.32 -.82 -.42 -.92", .065],
      ["M0 -.46 Q.2 -.49 .3 -.72 Q.39 -.86 .43 -.97", .065],
      ["M-.27 -.56 Q-.45 -.6 -.47 -.76", .048], ["M-.29 -.65 Q-.19 -.72 -.22 -.88", .046],
      ["M.3 -.64 Q.14 -.71 .08 -.85", .049], ["M.34 -.82 Q.49 -.8 .52 -.88", .042],
      ["M-.04 -.72 Q-.18 -.8 -.18 -.99", .038],
    ];
    for (const [d, width] of branches) path(ctx, d, width, gradient);
    for (const [d, width] of branches) path(ctx, d, width * .24, `${highlight}55`);
  } else if (piece.form === "tendrils") {
    path(ctx, "M0 .02 C-.07 -.18 -.38 -.32 -.39 -.57 C-.41 -.79 -.28 -.98 -.18 -.82 C-.08 -.69 -.22 -.58 -.28 -.71", .075, gradient);
    path(ctx, "M0 .01 C.19 -.14 .32 -.33 .38 -.53 C.5 -.82 .37 -1 .26 -.91 C.14 -.8 .21 -.68 .32 -.74", .069, gradient);
    path(ctx, "M0 -.02 C.04 -.3 -.16 -.54 -.08 -.78 C0 -1.12 .18 -.98 .13 -.85 C.09 -.74 -.03 -.83 .04 -.89", .066, gradient);
    path(ctx, "M-.02 -.2 C-.18 -.2 -.19 -.32 -.2 -.4 M.1 -.23 Q.26 -.23 .23 -.45", .045, gradient);
    path(ctx, "M0 .01 C.19 -.14 .32 -.33 .38 -.53", .015, `${highlight}77`);
  } else if (piece.form === "fan") {
    const outline = new Path2D("M0 .03 Q-.06 -.14 -.06 -.3 C-.24 -.32 -.53 -.45 -.55 -.6 C-.6 -.68 -.48 -.76 -.47 -.82 C-.45 -.93 -.31 -.87 -.25 -.94 C-.19 -1.05 -.1 -.98 0 -1.02 C.1 -1.08 .16 -.97 .26 -.97 C.37 -.99 .38 -.89 .47 -.84 C.54 -.82 .53 -.7 .57 -.63 C.62 -.49 .29 -.32 .07 -.3 Q.05 -.09 0 .03Z");
    ctx.fill(outline); ctx.save(); ctx.clip(outline);
    for (let i = 0; i < 19; i++) {
      const angle = -Math.PI + i / 18 * Math.PI, x = Math.cos(angle) * .65, y = -.3 + Math.sin(angle) * .8;
      path(ctx, `M0 0 Q${x * .16} -.45 ${x} ${y}`, .008, `${highlight}aa`);
      for (let j = 1; j <= 3; j++) {
        const fraction = j / 4;
        path(ctx, `M${x * fraction} ${-.3 + (y + .3) * fraction} q${.09 * Math.sign(x || 1)} -.09 ${.13 * Math.sign(x || 1)} -.12`, .004, `${highlight}99`);
      }
    }
    ctx.restore(); ctx.strokeStyle = `${highlight}cc`; ctx.lineWidth = .013; ctx.stroke(outline);
  } else if (piece.form === "plates") {
    path(ctx, "M0 .02 Q-.05 -.42 0 -.86", .095, gradient);
    for (let i = 0; i < 4; i++) {
      const y = -.2 - i * .215, width = .46 - i * .072;
      ctx.beginPath(); ctx.moveTo(-width, y);
      ctx.bezierCurveTo(-width * 1.2, y - .12, width * .92, y - .13, width, y - .04);
      ctx.bezierCurveTo(width * 1.18, y + .045, -width * .6, y + .12, -width, y);
      ctx.fillStyle = gradient; ctx.fill();
      ctx.strokeStyle = highlight; ctx.lineWidth = .021; ctx.stroke();
      path(ctx, `M${-width * .75} ${y - .024} Q0 ${y - .11} ${width * .75} ${y - .04}`, .009, `${highlight}77`);
    }
  } else if (piece.form === "cluster") {
    const lobes = [[-.24, -.22, .25], [.25, -.23, .25], [0, -.23, .3], [-.2, -.46, .23], [.2, -.48, .23], [0, -.63, .22]];
    for (const [x, y, r] of lobes) {
      const sphere = ctx.createRadialGradient(x - r * .32, y - r * .45, 0, x, y, r);
      sphere.addColorStop(0, mixCoralColors(color, highlight, .7)); sphere.addColorStop(.5, color); sphere.addColorStop(1, mixCoralColors(color, "#153b44", .4));
      ellipse(ctx, x, y, r, r * .85, sphere);
      for (let j = 0; j < 11; j++) {
        const a = j * 2.4, d = r * (.2 + j / 18);
        ellipse(ctx, x + Math.cos(a) * d, y + Math.sin(a) * d * .8, .012, .009, `${highlight}aa`);
      }
    }
  } else {
    for (const [x, height, width, lean] of [[-.28, .76, .17, -.05], [.27, .61, .19, .06], [0, 1, .21, -.02]]) {
      const tube = ctx.createLinearGradient(x - width / 2, 0, x + width / 2, 0);
      tube.addColorStop(0, mixCoralColors(color, "#17434a", .35)); tube.addColorStop(.45, color); tube.addColorStop(1, mixCoralColors(color, highlight, .5));
      ctx.beginPath(); ctx.moveTo(x - width / 2, 0); ctx.quadraticCurveTo(x - width / 2 + lean, -height * .6, x + lean - width / 2, -height);
      ctx.lineTo(x + lean + width / 2, -height); ctx.quadraticCurveTo(x + width / 2 + lean, -height * .6, x + width / 2, 0); ctx.closePath();
      ctx.fillStyle = tube; ctx.fill();
      for (let j = 1; j < 6; j++) path(ctx, `M${x - width * .36 + lean * j / 6} ${-height * j / 6} q${width * .35} .028 ${width * .72} 0`, .007, `${highlight}55`);
      ellipse(ctx, x + lean, -height, width * .52, .044, highlight);
      ellipse(ctx, x + lean, -height - .003, width * .36, .025, mixCoralColors(color, "#082f38", .72));
    }
  }
  ctx.restore();
}

function scenery(ctx: Ctx, seed: number) {
  const rng = random(seed);
  const water = ctx.createLinearGradient(0, 0, 0, REEF_HEIGHT);
  water.addColorStop(0, "#176775"); water.addColorStop(.45, "#0c414e"); water.addColorStop(1, "#082e3b");
  ctx.fillStyle = water; ctx.fillRect(0, 0, REEF_WIDTH, REEF_HEIGHT);
  const glow = ctx.createRadialGradient(630, -150, 0, 630, -150, 800);
  glow.addColorStop(0, "#b7eedf4a"); glow.addColorStop(.65, "#70c9c815"); glow.addColorStop(1, "#70c9c800"); ctx.fillStyle = glow; ctx.fillRect(0, 0, 1100, 720);
  for (let i = 0; i < 6; i++) {
    ctx.beginPath(); ctx.moveTo(400 + i * 110, 0); ctx.lineTo(80 + i * 110, 650); ctx.lineTo(210 + i * 130, 650); ctx.lineTo(430 + i * 110, 0);
    const ray = ctx.createLinearGradient(0, 0, 0, 650); ray.addColorStop(0, "#c4ffe312"); ray.addColorStop(1, "#c4ffe300"); ctx.fillStyle = ray; ctx.fill();
  }
  // Distant, low-contrast reef silhouettes give the scene depth.
  for (let i = 0; i < 23; i++) {
    const x = rng() * 1100, h = 22 + rng() * 110;
    path(ctx, `M${x} 618 q${rng() * 30 - 15} ${-h / 2} 0 ${-h} M${x} ${618 - h / 2} q-23 -4 -27 -35 M${x} ${600 - h / 3} q28 -4 33 -42`, 5 + rng() * 11, "#1c5b6260");
  }
  const sand = ctx.createLinearGradient(0, 605, 0, 720); sand.addColorStop(0, "#567f7a"); sand.addColorStop(.18, "#719185"); sand.addColorStop(1, "#3c6466");
  ctx.beginPath(); ctx.moveTo(0, 629); ctx.bezierCurveTo(350, 584, 730, 609, 1100, 612); ctx.lineTo(1100, 720); ctx.lineTo(0, 720); ctx.fillStyle = sand; ctx.fill();
  for (let i = 0; i < 150; i++) ellipse(ctx, rng() * 1100, 627 + rng() * 93, 1 + rng() * 2, .6, "#d7dbc52a");
  for (let i = 0; i < 6; i++) path(ctx, `M0 ${661 + i * 17} Q400 ${630 + i * 15} 1100 ${645 + i * 15}`, 1, "#d6e0c616");
  for (const x of [85, 145, 972, 1030]) {
    for (let i = 0; i < 7; i++) {
      const height = 35 + rng() * 85;
      path(ctx, `M${x + i * 3} 650 Q${x - 24 + i * 7} ${620 - height / 2} ${x - 20 + i * 8} ${650 - height}`, 2 + rng() * 3, i % 2 ? "#6baea280" : "#36867d90");
    }
  }
  ellipse(ctx, 280, 653, 26, 7, "#133f4540");
  ctx.fillStyle = "#bdc6a6"; ctx.fill(new Path2D("M264 651 Q270 626 289 630 Q300 637 294 649Z"));
  for (let i = 0; i < 5; i++) path(ctx, `M${268 + i * 6} 649 Q${274 + i * 4} 634 281 633`, 1, "#617f78");
}
function rocks(ctx: Ctx, seed: number) {
  const rng = random(seed + 91);
  ellipse(ctx, 550, 628, 257, 24, "#04232c70");
  for (const [x, y, w, h] of [[383, 594, 93, 55], [729, 592, 102, 53], [540, 588, 147, 75], [624, 593, 112, 64], [475, 605, 97, 53]]) {
    const rock = ctx.createLinearGradient(x, y - h, x, y + h); rock.addColorStop(0, "#94aea0"); rock.addColorStop(.25, "#6b8983"); rock.addColorStop(1, "#2c5157");
    ctx.beginPath(); ctx.moveTo(x - w, y + 14); ctx.bezierCurveTo(x - w * .95, y - h * .55, x - w * .3, y - h * .78, x, y - h * .66);
    ctx.bezierCurveTo(x + w * .45, y - h, x + w * .95, y - h * .1, x + w, y + 14); ctx.quadraticCurveTo(x, y + h * .55, x - w, y + 14);
    ctx.fillStyle = rock; ctx.fill();
    path(ctx, `M${x - w * .78} ${y - h * .15} Q${x - w * .3} ${y - h * .65} ${x + w * .35} ${y - h * .45}`, 2.5, "#b4c7ab55");
    for (let i = 0; i < 16; i++) ellipse(ctx, x + (rng() - .5) * w * 1.3, y - rng() * h * .35, 1 + rng() * 3, 1, "#173e4830");
  }
  for (let i = 0; i < 12; i++) ellipse(ctx, 330 + rng() * 440, 633 + rng() * 19, 5 + rng() * 13, 3 + rng() * 5, "#557b77");
}
function fish(ctx: Ctx, index: number, time: number, seed: number, opacity: number) {
  const rng = random(seed + index * 971); const phase = rng() * TAU;
  const kind = index % 3, speed = .065 + rng() * .028, a = phase + time * speed;
  const x = 550 + Math.sin(a) * (270 + rng() * 180), y = 245 + (index % 4) * 49 + Math.sin(a * 1.8 + phase) * 23;
  ctx.save(); ctx.globalAlpha = opacity; ctx.translate(x, y); ctx.scale(Math.cos(a) >= 0 ? 1 : -1, 1);
  const size = .65 + rng() * .4; ctx.scale(size, size);
  const tail = Math.sin(time * 1.8 + phase) * 3;
  if (kind === 0) {
    ctx.fillStyle = "#ffd484"; ctx.fill(new Path2D(`M-20 0 L-38 ${-15 + tail} Q-34 0 -38 ${15 + tail}Z`));
    ellipse(ctx, 0, 0, 25, 15, "#f8a262");
    ctx.save(); ctx.beginPath(); ctx.ellipse(0, 0, 25, 15, 0, 0, TAU); ctx.clip();
    for (const stripe of [-14, 0, 13]) { ctx.fillStyle = "#fff3d3"; ctx.fillRect(stripe, -20, 5, 40); }
    ctx.restore(); path(ctx, "M-10 -12 Q0 -24 13 -12", 3, "#ffca77");
  } else if (kind === 1) {
    ctx.fillStyle = "#f4dd86"; ctx.fill(new Path2D(`M-20 0 L-38 ${-13 + tail} L-36 ${14 + tail}Z`));
    ctx.fillStyle = "#f2cc65"; ctx.fill(new Path2D("M-23 0Q-12 -30 9 -18Q31 -3 23 7Q-4 30 -23 0Z"));
    path(ctx, "M2 -21 Q8 2 0 21", 7, "#193d54"); path(ctx, "M-9 -16 L-15 9", 2, "#fff1a0");
    ctx.fillStyle = "#ffdf8b88"; ctx.fill(new Path2D("M0 0L-6 14L12 6Z"));
  } else {
    ctx.fillStyle = "#59bbc8"; ctx.fill(new Path2D(`M-27 0L-43 ${-12 + tail}L-43 ${12 + tail}Z`));
    ellipse(ctx, 0, 0, 30, 11, "#79cbe0"); path(ctx, "M-24 1 Q0 4 24 0", 3, "#b9a2fa");
    ctx.fillStyle = "#5aa5c5"; ctx.fill(new Path2D("M-9 -9Q0 -24 15 -10Z"));
  }
  ellipse(ctx, 17, -3, 2.4, 2.4, "#103c49"); ellipse(ctx, 17.6, -3.8, .8, .8, "#fff7d8");
  ctx.restore();
}

export class ReefRenderer {
  private background: HTMLCanvasElement;
  private reef: HTMLCanvasElement;
  private seed = -1;
  private pieces: ReefState["pieces"] | null = null;
  private preview: CoralIntent | null = null;
  private placement = 1;
  constructor(private canvas: HTMLCanvasElement) {
    this.background = canvas.ownerDocument.createElement("canvas"); this.reef = canvas.ownerDocument.createElement("canvas");
    for (const layer of [this.background, this.reef]) { layer.width = 1650; layer.height = 1080; }
  }
  render(state: ReefState, preview: CoralIntent | null, time = 0, placement = 1) {
    if (this.seed !== state.seed) {
      const ctx = this.background.getContext("2d")!; ctx.setTransform(1.5, 0, 0, 1.5, 0, 0); scenery(ctx, state.seed); this.seed = state.seed; this.pieces = null;
    }
    if (this.pieces !== state.pieces || this.preview !== preview || placement !== this.placement) {
      const ctx = this.reef.getContext("2d")!; ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, 1650, 1080); ctx.scale(1.5, 1.5); rocks(ctx, state.seed);
      const neighbors: CoralIntent[] = preview ? [...state.pieces, preview] : state.pieces;
      state.pieces.forEach((piece, i) => {
        ctx.save();
        if (i === state.pieces.length - 1 && placement < 1) { ctx.translate(piece.x, piece.y); ctx.scale(.92 + .08 * placement, .92 + .08 * placement); ctx.translate(-piece.x, -piece.y); }
        drawCoral(ctx, piece, coralHighlight(piece, neighbors)); ctx.restore();
      });
      this.pieces = state.pieces; this.preview = preview; this.placement = placement;
    }
    const ctx = this.canvas.getContext("2d")!;
    const scale = Math.min(this.canvas.width / REEF_WIDTH, this.canvas.height / REEF_HEIGHT);
    ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.fillStyle = "#082f3b"; ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.translate((this.canvas.width - REEF_WIDTH * scale) / 2, (this.canvas.height - REEF_HEIGHT * scale) / 2); ctx.scale(scale, scale);
    ctx.drawImage(this.background, 0, 0, REEF_WIDTH, REEF_HEIGHT);
    const rng = random(state.seed + 732);
    // Fixed caps: 24 suspended motes, 7 bubbles, at most 12 visiting fish.
    for (let i = 0; i < 24; i++) {
      const x = (rng() * 1100 + time * (1.5 + rng()) + Math.sin(time * .12 + i) * 4) % 1100;
      const y = (rng() * 590 - time * .7 + 59000) % 590;
      ellipse(ctx, x, y, .7 + rng(), .7 + rng(), "#bfe6d82b");
    }
    for (let i = 0; i < 7; i++) {
      const x = 210 + rng() * 700 + Math.sin(time * .25 + i) * 8, y = 620 - (rng() * 550 + time * 13) % 570;
      ctx.beginPath(); ctx.arc(x, y, 1.5 + rng() * 3, 0, TAU); ctx.strokeStyle = "#b4e9dd38"; ctx.lineWidth = .8; ctx.stroke();
    }
    ctx.save();
    // A tiny shared sway keeps coral alive; placement stays still for precise attachments.
    if (!preview) { ctx.translate(Math.sin(time * .65) * .7, 0); ctx.transform(1, 0, Math.sin(time * .5) * .0016, 1, -Math.sin(time * .5) * .0016 * 560, 0); }
    ctx.drawImage(this.reef, 0, 0, REEF_WIDTH, REEF_HEIGHT); ctx.restore();
    const count = reefFishCount(state.pieces);
    for (let i = 0; i < count; i++) fish(ctx, i, time, state.seed, preview ? .65 : .9);
    if (!preview && !state.admire && state.pieces.length === 0) {
      for (const point of BASE_ATTACHMENTS) {
        ctx.beginPath(); ctx.arc(point.x, point.y, 3, 0, TAU); ctx.fillStyle = "#d5ffe280"; ctx.fill();
      }
    }
    if (preview) {
      const nearest = nearestAttachment(state.pieces, preview), attached = nearest.distance <= 38;
      for (const point of reefAttachments(state.pieces)) {
        if (Math.hypot(point.x - preview.x, point.y - preview.y) > 170 && point.piece !== null) continue;
        ctx.beginPath(); ctx.arc(point.x, point.y, point === nearest.point ? 5 : 3.5, 0, TAU);
        ctx.fillStyle = "#d5ffe27a"; ctx.fill();
      }
      if (nearest.distance > 1 && nearest.distance < 230) {
        ctx.setLineDash([4, 6]); path(ctx, `M${preview.x} ${preview.y} L${nearest.point.x} ${nearest.point.y}`, 1.2, "#d5ffe290"); ctx.setLineDash([]);
      }
      ctx.save(); ctx.globalAlpha = attached ? .85 : .48; drawCoral(ctx, preview, coralHighlight(preview, state.pieces)); ctx.restore();
      ctx.beginPath(); ctx.arc(preview.x, preview.y, 9, 0, TAU); ctx.strokeStyle = attached ? "#d8ffca" : "#f4d5a6"; ctx.lineWidth = 2; ctx.stroke();
      ctx.beginPath(); ctx.arc(preview.x, preview.y, 3, 0, TAU); ctx.fillStyle = attached ? "#d8ffca" : "#f4d5a6"; ctx.fill();
    }
  }
}
export function reefExportCanvas(state: ReefState): HTMLCanvasElement {
  const canvas = document.createElement("canvas"); canvas.width = 2200; canvas.height = 1440;
  new ReefRenderer(canvas).render(state, null, 18);
  return canvas;
}
